import React from 'react';
import { cn } from '../../lib/utils';
import { CalendarIcon, CalendarDaysIcon } from 'lucide-react';
interface MetricsDateFilterProps {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
}
type PresetKey = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'last3Months';
interface Preset {
  key: PresetKey;
  label: string;
}
const PRESETS: Preset[] = [{
  key: 'today',
  label: 'Hoy'
}, {
  key: 'yesterday',
  label: 'Ayer'
}, {
  key: 'thisWeek',
  label: 'Esta Semana'
}, {
  key: 'thisMonth',
  label: 'Este Mes'
}, {
  key: 'lastMonth',
  label: 'Último Mes'
}, {
  key: 'last3Months',
  label: '3 Meses'
}];
function getPresetDates(key: PresetKey): {
  start: string;
  end: string;
} {
  // Anclar SIEMPRE el "hoy" al día calendario de COSTA RICA (UTC-6), igual
  // que el resto del Cierre de Caja (todayIso() y la ventana -06:00). Antes
  // se usaba new Date() del navegador: si la PC del cajero (o el server del
  // preview) está en otra zona, el "hoy" del preset no coincidía con el día
  // que realmente consulta la caja, y rangos como "Este Mes" / "Último Mes"
  // se armaban sobre el mes equivocado (o aparentaban vacíos). Construimos
  // `today` a partir de los componentes de fecha de Costa Rica para que los
  // límites de semana/mes se calculen sobre el calendario CR correcto.
  const crParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Costa_Rica',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const crGet = (type: string) => Number(crParts.find(p => p.type === type)?.value);
  const today = new Date(crGet('year'), crGet('month') - 1, crGet('day'));
  // Formatear con los componentes LOCALES del Date (NO UTC). `today` ya
  // contiene los valores Y/M/D de Costa Rica, así que esto produce el
  // YYYY-MM-DD del día CR. (Usar toISOString() volvía a convertir a UTC y
  // corría el día en navegadores al este de UTC.)
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  switch (key) {
    case 'today':
      return {
        start: fmt(today),
        end: fmt(today)
      };
    case 'yesterday':
      {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: fmt(yesterday),
          end: fmt(yesterday)
        };
      }
    case 'thisWeek':
      {
        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        return {
          start: fmt(monday),
          end: fmt(today)
        };
      }
    case 'thisMonth':
      {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          start: fmt(firstDay),
          end: fmt(today)
        };
      }
    case 'lastMonth':
      {
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          start: fmt(firstDay),
          end: fmt(lastDay)
        };
      }
    case 'last3Months':
      {
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        return {
          start: fmt(firstDay),
          end: fmt(today)
        };
      }
  }
}
function getActivePreset(startDate: string, endDate: string): PresetKey | null {
  for (const preset of PRESETS) {
    const {
      start,
      end
    } = getPresetDates(preset.key);
    if (start === startDate && end === endDate) return preset.key;
  }
  return null;
}
export function MetricsDateFilter({
  startDate,
  endDate,
  onChange
}: MetricsDateFilterProps) {
  const activePreset = getActivePreset(startDate, endDate);
  return <div className="space-y-3" data-id="element-1252" data-mp="1yna68oewx0ig">
      {/* Preset Buttons */}
      <div className="flex flex-wrap gap-2" data-id="element-1253" data-mp="1ylqrx3ctgzjb">
        {PRESETS.map(preset => <button key={preset.key} onClick={() => {
        const {
          start,
          end
        } = getPresetDates(preset.key);
        onChange(start, end);
      }} className={cn('px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-150', activePreset === preset.key ? 'bg-gray-900 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-900')} data-id="element-1254" data-mp="1j490etc1vy31">
            {preset.label}
          </button>)}
      </div>

      {/* Custom Date Inputs */}
      <div className="flex items-center gap-3 flex-wrap" data-id="element-1255" data-mp="1s3vzh8hb4rsx">
        <div className="flex items-center gap-2" data-id="element-1256" data-mp="1x407sb82ag82">
          <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-1257" data-mp="1x2p5ygy6fijl" />
          <label className="text-sm text-gray-500 font-medium" data-id="element-1258" data-mp="2ktbcg3sj8urh">Desde</label>
          <input type="date" value={startDate} onChange={e => onChange(e.target.value, endDate)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300" data-id="element-1259" data-mp="2pm4g0oc9lx1z" />
        </div>
        <div className="flex items-center gap-2" data-id="element-1260" data-mp="nxzvw2authjg">
          <CalendarDaysIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-1261" data-mp="iy547wj98nxc" />
          <label className="text-sm text-gray-500 font-medium" data-id="element-1262" data-mp="3rcdqkmv6oih2">Hasta</label>
          <input type="date" value={endDate} onChange={e => onChange(startDate, e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300" data-id="element-1263" data-mp="3reqfjjzczt3m" />
        </div>
      </div>
    </div>;
}